import { Divider, Popover } from 'antd'
import cls from 'classnames'
import React from 'react'

import { Watermark } from '@/components'
import {
  PaginationInput,
  ParentCommentsFragment,
  PostType,
  RequestedPostFragment,
  usePostUpdatedSubscription,
} from '@/generated/graphql'

import { PostCardAction } from './post-card-action.component'
import { PostCardHeader } from './post-card-header.component'
import { useReadPost } from './post-card.hooks'
import s from './post-card.module.less'
import { PostComments } from './post-comments'
import { PostCardContent } from './post-contents/post-card-contents.component'
import { PostPerformance } from './post-performance/post-performance.component'

interface PostCardProps {
  post: RequestedPostFragment & ParentCommentsFragment
  isGroup?: boolean
  isUser?: boolean
  isSection?: boolean
  isTop?: boolean
  isSinglePost?: boolean
  isAllPosts?: boolean
  isCommentControllable?: boolean
  isRecommended?: boolean
  disableAction?: boolean
  disableContentPics?: boolean
  disableContentBackground?: boolean
  disableCreateComment?: boolean
  hideHeaderMenu?: boolean
  parentCommentPagination?: PaginationInput
  childCommentPagination?: PaginationInput
}

export const PostCard: React.FC<PostCardProps> = ({
  post,
  isGroup = false,
  isUser = false,
  isSection = false,
  isTop = false,
  isSinglePost = false,
  isRecommended = false,
  isAllPosts = false,
  isCommentControllable = false,
  disableAction = false,
  disableContentPics = false,
  disableContentBackground = false,
  disableCreateComment = false,
  hideHeaderMenu = false,
  parentCommentPagination,
  childCommentPagination,
}) => {
  const [showCreateComment, setShowCreateComment] = React.useState(false)
  const [showMoreComment, setShowMoreComment] = React.useState(false)
  const [loadAndShowComment, setLoadAndShowComment] = React.useState(false)
  const ref = useReadPost(post)

  usePostUpdatedSubscription({ variables: { postId: post.postId } })

  return (
    <Watermark ref={ref}>
      <div
        className={cls(
          s.container,
          post.postType === PostType.Honor ? s.honoredBorder : isTop ? s.pinnedBorder : s.normalBorder
        )}
      >
        <PostCardHeader
          post={post}
          isRecommended={isRecommended}
          isGroup={isGroup}
          isUser={isUser}
          isSection={isSection}
          showGroupInfo={!isGroup}
          isTop={isTop}
          hideMenu={hideHeaderMenu}
        />
        <PostCardContent
          post={post}
          isGroup={isGroup}
          isUser={isUser}
          isSection={isSection}
          isSinglePost={isSinglePost}
          isRecommended={isRecommended}
          disablePics={disableContentPics}
          disableBackground={disableContentBackground}
        />
        <PostPerformance
          onClickComment={() => (setShowCreateComment(true), setLoadAndShowComment(true))}
          post={post}
          isSinglePost={isSinglePost}
          showReadCount={isAllPosts}
        />
        <Divider className={s.divider} />
        {!disableAction && (
          <PostCardAction
            post={post}
            onClickComment={() => (setShowCreateComment(true), setShowMoreComment(true))}
            disableActionText={isRecommended}
          />
        )}
        <PostComments
          post={post}
          showCreateComment={isSinglePost || showCreateComment}
          showMoreComment={showMoreComment}
          loadAndShowComment={loadAndShowComment}
          isCommentControllable={isCommentControllable}
          disableCreate={disableCreateComment}
          parentCommentPagination={parentCommentPagination}
          childCommentPagination={childCommentPagination}
        />
      </div>
    </Watermark>
  )
}
interface PostCardActionHoverProps {
  post: RequestedPostFragment & ParentCommentsFragment
  setShowCreateComment: (value: React.SetStateAction<boolean>) => void
  setShowMoreComment: (value: React.SetStateAction<boolean>) => void
}
export const PostCardActionHover: React.FC<PostCardActionHoverProps> = ({
  post,
  setShowCreateComment,
  setShowMoreComment,
  children,
}) => {
  const [isVisible, setIsVisible] = React.useState(false)

  const content = (
    <div className={s.hoverActions}>
      <PostCardAction
        post={post}
        onClickComment={() => (setShowCreateComment(true), setShowMoreComment(true))}
        disableActionText={true}
      />
    </div>
  )

  return (
    <Popover
      destroyTooltipOnHide
      visible={isVisible}
      onVisibleChange={(visible) => setIsVisible(visible)}
      placement='rightTop'
      trigger={['hover']}
      content={content}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
    >
      {children}
    </Popover>
  )
}
